<script setup>
import { ref } from 'vue'
import { useUserStore } from '../stores/userStore'
import UserCard from '../components/UserCard.vue'
import StatCard from '../components/StatCard.vue'
import AddTraderModal from '../components/AddTraderModal.vue'

const userStore = useUserStore()
const traders = userStore.getByRole('trader')

const stats = {
  totalTraders: {
    value: traders.length,
    change: 8,
    trend: 'up',
    icon: 'user-tie'
  },
  activeTraders: {
    value: traders.filter(t => t.status === 'active').length,
    change: 3,
    trend: 'up',
    icon: 'chart-line'
  },
  tradingVolume: {
    value: 1250,
    change: 12,
    trend: 'up',
    icon: 'chart-bar'
  }
}

const searchQuery = ref('')
const filters = ref({
  status: 'all',
  accessLevel: 'all'
})

const viewMode = ref('grid') // 'grid' or 'list'
const showAddTraderModal = ref(false)

const toggleView = (mode) => {
  viewMode.value = mode
}
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold">Traders</h1>
        <p class="text-gray-500">Dashboard / Traders</p>
      </div>
      
      <div class="flex gap-3">
        <button class="btn-secondary">
          <i class="fas fa-filter mr-2"></i> Filter
        </button>
        <button class="btn-secondary">
          <i class="fas fa-download mr-2"></i> Export
        </button>
        <button 
          @click="showAddTraderModal = true"
          class="btn-primary bg-pink-500 hover:bg-pink-600"
        >
          + Add Trader
        </button>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-3 gap-6 mb-8">
      <StatCard
        v-for="(stat, key) in stats"
        :key="key"
        :title="key.replace(/([A-Z])/g, ' $1').trim()"
        :value="stat.value"
        :change="stat.change"
        :icon="stat.icon"
        period="vs last month"
      />
    </div>

    <!-- Search and Filters -->
    <div class="flex gap-4 mb-6">
      <div class="flex-1 relative">
        <input 
          v-model="searchQuery"
          type="text"
          placeholder="Search traders..."
          class="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
        >
        <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
      </div>
      
      <select 
        v-model="filters.status"
        class="px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
      >
        <option value="all">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
      
      <select 
        v-model="filters.accessLevel"
        class="px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
      >
        <option value="all">All Access Levels</option>
        <option value="basic">Basic</option>
        <option value="advanced">Advanced</option>
        <option value="expert">Expert</option>
      </select>
    </div>

    <!-- View Toggle -->
    <div class="flex justify-end mb-4">
      <div class="bg-gray-100 rounded-lg p-1">
        <button 
          @click="toggleView('grid')"
          class="px-3 py-1 rounded-lg"
          :class="viewMode === 'grid' ? 'bg-white shadow' : ''"
        >
          <i class="fas fa-th-large"></i>
        </button>
        <button 
          @click="toggleView('list')"
          class="px-3 py-1 rounded-lg"
          :class="viewMode === 'list' ? 'bg-white shadow' : ''"
        >
          <i class="fas fa-list"></i>
        </button>
      </div>
    </div>

    <!-- Grid View -->
    <div v-if="viewMode === 'grid'" class="grid grid-cols-3 gap-6">
      <UserCard 
        v-for="trader in traders"
        :key="trader.id"
        :user="trader"
      />
    </div>

    <!-- List View -->
    <div v-else class="bg-white rounded-lg shadow">
      <table class="w-full">
        <thead class="bg-gray-800 text-white">
          <tr>
            <th class="py-3 px-4 text-left">User</th>
            <th class="py-3 px-4 text-left">Status</th>
            <th class="py-3 px-4 text-left">Work Location</th>
            <th class="py-3 px-4 text-left">Access Level</th>
            <th class="py-3 px-4 text-left">Last Active</th>
            <th class="py-3 px-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <tr v-for="trader in traders" :key="trader.id" class="hover:bg-gray-50">
            <td class="py-3 px-4">
              <div class="flex items-center">
                <div class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <i class="fas fa-user-tie text-gray-500"></i>
                </div>
                <div class="ml-3">
                  <div class="font-medium">{{ trader.name }}</div>
                  <div class="text-sm text-gray-500">{{ trader.email }}</div>
                </div>
              </div>
            </td>
            <td class="py-3 px-4">
              <span 
                class="px-2 py-1 rounded-full text-sm"
                :class="trader.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'"
              >
                {{ trader.status }}
              </span>
            </td>
            <td class="py-3 px-4 capitalize">{{ trader.workLocation }}</td>
            <td class="py-3 px-4 capitalize">{{ trader.accessLevel }}</td>
            <td class="py-3 px-4 text-sm text-gray-500">{{ trader.lastActive }}</td>
            <td class="py-3 px-4">
              <div class="flex justify-end gap-2">
                <button class="text-gray-400 hover:text-gray-600">
                  <i class="fas fa-eye"></i>
                </button>
                <button class="text-gray-400 hover:text-gray-600">
                  <i class="fas fa-edit"></i>
                </button>
                <button class="text-gray-400 hover:text-red-600">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add Trader Modal -->
    <AddTraderModal 
      :show="showAddTraderModal"
      @close="showAddTraderModal = false"
    />
  </div>
</template>

<style scoped>
.btn-primary {
  @apply px-4 py-2 text-white rounded-lg transition-colors duration-200;
}

.btn-secondary {
  @apply px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg 
         hover:bg-gray-50 transition-colors duration-200;
}
</style> 